import {
  DateValue,
  useButton,
  useCalendar,
  useCalendarCell,
  useCalendarGrid,
  useLocale,
} from 'react-aria'
import { CalendarState, useCalendarState } from 'react-stately'
import {
  CalendarDate,
  createCalendar,
  getWeeksInMonth,
} from '@internationalized/date'
import { Button } from '../Button/Button'
import { useRef } from 'react'
import styled, { css } from 'styled-components'
import { FirstDayOfWeek } from './types'

const CalendarContainer = styled.div`
  width: 230px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  > table {
    width: 100%;
  }
`

const Header = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  > h2 {
    flex: 1;
    margin: 0;
  }
`

// TODO: Disable button color
const ButtonContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;

  > button {
    font-family: ${({ theme }) => theme.fonts.primary};
    text-align: center;
    width: 35px;
    height: 35px;
    padding: 5px;
    font-weight: bold;
    border-radius: 4px;
  }
`

interface Props {
  firstDayOfWeek?: FirstDayOfWeek
  minValue?: DateValue | null
  maxValue?: DateValue | null
}

export const Calendar: React.FC<Props> = (props) => {
  const { locale } = useLocale()
  const state = useCalendarState({ ...props, locale, createCalendar })

  const { calendarProps, prevButtonProps, nextButtonProps, title } =
    useCalendar(props, state)

  const next = useRef(null)
  const prev = useRef(null)
  const { buttonProps: nextProps } = useButton(nextButtonProps, next)
  const { buttonProps: prevProps } = useButton(prevButtonProps, prev)

  return (
    <CalendarContainer {...calendarProps}>
      <Header>
        <h2>{title}</h2>
      </Header>
      <CalendarGrid
        state={state}
        firstDayOfWeek={props.firstDayOfWeek}
        locale={locale}
      />
      <ButtonContainer>
        <Button {...prevProps} data-testid="Calendar__prevButton">
          &lt;
        </Button>
        <Button {...nextProps} data-testid="Calendar__nextButton">
          &gt;
        </Button>
      </ButtonContainer>
    </CalendarContainer>
  )
}

interface GridProps {
  state: CalendarState
  firstDayOfWeek?: FirstDayOfWeek
  locale: string
}
const CalendarGrid: React.FC<GridProps> = ({
  state,
  locale,
  firstDayOfWeek = 'sun',
}) => {
  const { gridProps, headerProps, weekDays } = useCalendarGrid(
    { firstDayOfWeek },
    state,
  )

  const weeksInMonth = getWeeksInMonth(state.visibleRange.start, locale)

  return (
    <table {...gridProps}>
      <thead {...headerProps}>
        <tr>
          {weekDays.map((day, index) => (
            <th key={index}>{day}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {[...new Array(weeksInMonth).keys()].map((weekIndex) => (
          <tr key={weekIndex}>
            {state
              .getDatesInWeek(weekIndex)
              .map((date, i) =>
                date ? (
                  <CalendarCell key={i} state={state} date={date} />
                ) : (
                  <td key={i} />
                ),
              )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

const Cell = styled.div<{
  $isSelected: boolean
  $isDisabled: boolean
  $isUnavailable: boolean
}>`
  cursor: pointer;
  text-align: center;
  background: ${({ $isSelected, theme }) =>
    $isSelected && theme.colors.font.regular};
  padding: 2px;
  border-radius: 4px;
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      font-weight: bold;
    `}

  color: ${({ $isDisabled, $isUnavailable, $isSelected, theme }) =>
    $isDisabled
      ? theme.colors.font.disabled
      : $isUnavailable
        ? theme.colors.font.error
        : $isSelected
          ? theme.colors.font.white
          : theme.colors.font.regular};
`

interface CellProps {
  state: CalendarState
  date: CalendarDate
}

const CalendarCell: React.FC<CellProps> = ({ state, date }) => {
  const ref = useRef(null)
  const {
    cellProps,
    buttonProps,
    isSelected,
    isOutsideVisibleRange,
    isDisabled,
    isUnavailable,
    formattedDate,
  } = useCalendarCell({ date }, state, ref)

  return (
    <td {...cellProps}>
      <Cell
        {...buttonProps}
        ref={ref}
        hidden={isOutsideVisibleRange}
        $isSelected={isSelected}
        $isDisabled={isDisabled}
        $isUnavailable={isUnavailable}
      >
        {formattedDate}
      </Cell>
    </td>
  )
}
